import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { Stack, Tooltip, Typography } from "@mui/material";
import { GridValidRowModel } from "@mui/x-data-grid-premium";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";

import { PortalEhsCell } from "./PortalEhsCell";

type PortalEhsChemicalRef =
  | { isEhs?: boolean | null; portalIsEhs?: boolean | null }
  | null
  | undefined;

type PortalEhsRow = { chemical?: PortalEhsChemicalRef };

/**
 * The states that show the column. A subset of encamp-shared's
 * `PORTAL_EHS_STATES`: CERS asks for a different answer than the app's for a
 * mixture, so a CA row has two correct values to show side by side. SD's answer
 * diverges only for a chemical whose EHS component was never marked, which the
 * compliance engine raises as a data error.
 */
const COLUMN_STATES = ["CA"];

const HEADER_NAME = "Reported as EHS";

const HEADER_TOOLTIP =
  "What CERS receives for this chemical. Most CUPAs want a mixture submitted as not EHS, " +
  "with only its EHS components marked; pure chemicals stay EHS.";

/**
 * The "Reported as EHS" column, for the chemical grids that already show the
 * app's own EHS mark. Empty everywhere else, so a grid can spread it in
 * unconditionally. A grid whose rows are the chemicals themselves, rather than
 * inventory rows carrying a `chemical`, passes a getter.
 */
export function portalEhsColumns<R extends PortalEhsRow>(
  state: string | null | undefined
): OmnisearchGridColDef<R>[];
export function portalEhsColumns<R extends GridValidRowModel>(
  state: string | null | undefined,
  getChemical: (row: R) => PortalEhsChemicalRef
): OmnisearchGridColDef<R>[];
export function portalEhsColumns<R extends GridValidRowModel>(
  state: string | null | undefined,
  getChemical: (row: R) => PortalEhsChemicalRef = (row) => row.chemical
): OmnisearchGridColDef<R>[] {
  if (!COLUMN_STATES.includes(state ?? "")) return [];
  return [
    {
      field: "portalIsEhs",
      headerName: HEADER_NAME,
      // No omnisearch key backs this value on the live or the snapshot read
      // path, and an unregistered key is dropped server-side: a filter here
      // would add a chip and change nothing.
      filterable: false,
      align: "center",
      headerAlign: "center",
      sortable: false,
      flex: 1,
      renderHeader: () => (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="body2" fontWeight={500}>
            {HEADER_NAME}
          </Typography>
          <Tooltip placement="top" title={HEADER_TOOLTIP}>
            <InfoOutlined sx={{ fontSize: 16, color: "text.secondary" }} />
          </Tooltip>
        </Stack>
      ),
      renderCell: ({ row }) => <PortalEhsCell state={state} chemical={getChemical(row)} />,
    },
  ];
}
